import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { floorDetails, projectPDF } from '../data/mock';
import { FileText, Download, Waves, Sun, Wind, Thermometer, Lightbulb } from 'lucide-react';

const FloorPlansSection = () => {
  const { language } = useLanguage();
  const t = floorDetails[language];

  const featureIcons = [Waves, Sun, Wind, Thermometer, Lightbulb];

  return (
    <section id="floor-plans" className="py-24 lg:py-32 bg-stone-100">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mb-4">
            {t.title}
          </h2>
          <p className="text-stone-500 text-lg mb-2">
            {language === 'es' ? 'Superficie total construida' : 'Total built area'}: <span className="font-semibold text-stone-800">{t.totalArea}</span>
          </p>
        </div>

        {/* Floor Cards - 4 columns for 4 floors */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {t.floors.map((floor, index) => (
            <div
              key={index}
              className="bg-white p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg text-stone-800">
                  {floor.name}
                </h3>
                <span className="text-amber-700 font-semibold text-sm">
                  {floor.area}
                </span>
              </div>
              <ul className="space-y-1.5">
                {floor.rooms.map((room, roomIndex) => (
                  <li
                    key={roomIndex}
                    className="text-stone-600 text-xs flex items-center"
                  >
                    <span className="w-1 h-1 bg-amber-700 rounded-full mr-2 flex-shrink-0" />
                    {room}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="bg-white p-8 mb-12">
          <h3 className="font-serif text-xl text-stone-800 mb-6 text-center">
            {language === 'es' ? 'Características Destacadas' : 'Key Features'}
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {t.features.map((feature, index) => {
              const Icon = featureIcons[index] || Sun;
              return (
                <div
                  key={index}
                  className="flex items-center text-stone-600 text-sm bg-stone-50 px-4 py-2"
                >
                  <Icon size={16} className="mr-2 text-amber-700" />
                  {feature}
                </div>
              );
            })}
          </div>
        </div>

        {/* Download Plans Button */}
        <div className="text-center">
          <a
            href={projectPDF.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-stone-800 text-white text-sm tracking-widest uppercase hover:bg-stone-700 transition-colors"
          >
            <FileText size={18} className="mr-3" />
            {language === 'es' ? 'Ver Planos Completos' : 'View Full Plans'}
            <Download size={16} className="ml-3" />
          </a>
          <p className="text-stone-500 text-sm mt-4">
            {language === 'es' ? 'Documento PDF con planos arquitectónicos detallados' : 'PDF document with detailed architectural plans'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default FloorPlansSection;
